import { DependencyList, MutableRefObject, useEffect, useState } from 'react';
import useOnMount from './useOnMount';
import usePersistFn from '@utils/hooks/usePersistFn';
import throttle from '@utils/throttle';
import ElementUtil, { BasicTarget } from '@utils/elementUtil';

const onScroll =
  (cb: Function, offset = 0) =>
  () => {
    const scrollHeight = Math.max(document.documentElement.scrollHeight, document.body.scrollHeight);
    const scrollTop = Math.max(document.documentElement.scrollTop, document.body.scrollTop);
    const clientHeight = document.documentElement.clientHeight;

    if (scrollTop + clientHeight + offset >= scrollHeight) {
      cb();
    }
  };

export default function useScroll(cb: Function, offset = 0, wait = 0, deps: DependencyList = []) {
  useEffect(() => {
    const boundOnScroll = throttle(onScroll(cb, offset), wait);
    document.addEventListener('scroll', boundOnScroll);
    return () => {
      document.removeEventListener('scroll', boundOnScroll);
    };
  }, [...deps]);
}

type ScrollDir = 'UP' | 'DOWN';
interface useScrollDirProps {
  el?: MutableRefObject<any>;
  onScrollUp?: () => void;
  scrollUpTop?: number;
  onScrollDown?: () => void;
  scrollDownTop?: number;
  wait?: number;
  refreshAutoEvent?: boolean;
}

export function useScrollDir(props: useScrollDirProps) {
  let lastScrollTop = 0;
  const { el, onScrollUp, scrollUpTop, onScrollDown, scrollDownTop, refreshAutoEvent = true } = props;

  const scrollEvent = () => {
    if (!el || !el.current) return;
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const scrollDir: ScrollDir = scrollTop > lastScrollTop ? 'DOWN' : 'UP';
    const elTop = el?.current.getBoundingClientRect().top;
    const windHeight = window.innerHeight;
    // const absoluteTop = scrollTop + elTop;

    if (onScrollUp && scrollDir === 'UP') {
      if (scrollUpTop) elTop - windHeight <= windHeight * scrollUpTop && onScrollUp();
      else onScrollUp();
    }

    if (onScrollDown && scrollDir === 'DOWN') {
      if (scrollDownTop) elTop - windHeight > windHeight * scrollDownTop && onScrollDown();
      else onScrollDown();
    }

    lastScrollTop = scrollTop <= 0 ? 0 : scrollTop;
  };

  const onScroll = throttle(scrollEvent, props.wait || 50);

  useOnMount(() => {
    if (refreshAutoEvent) {
      lastScrollTop = 0;
      scrollEvent();
    } else {
      lastScrollTop = window.pageYOffset || document.documentElement.scrollTop;
    }

    document.addEventListener('scroll', onScroll, false);

    return () => {
      document.removeEventListener('scroll', onScroll);
    };
  });
}

export function useScrollDocOffset(cb: Function, offset = 0, wait = 0, deps: DependencyList = []) {
  const scrollEvent =
    (cb: Function, offset = 0) =>
    () => {
      const scrollTop = Math.max(document.documentElement.scrollTop, document.body.scrollTop);

      if (offset <= scrollTop) cb();
    };

  useEffect(() => {
    const boundOnScroll = throttle(scrollEvent(cb, offset), wait);
    document.addEventListener('scroll', boundOnScroll, true);
    return () => {
      document.removeEventListener('scroll', boundOnScroll, true);
    };
  }, [...deps]);
}

export function useMousewheel(cb: Function, offset = 0, wait = 0) {
  const mousewheelEvent = () => {
    const scrollTop = Math.max(document.documentElement.scrollTop, document.body.scrollTop);

    if (offset >= scrollTop) cb();
  };

  const onMousewheel = throttle(mousewheelEvent, wait || 50);

  useOnMount(() => {
    document.addEventListener('mousewheel', onMousewheel, false);

    return () => {
      document.removeEventListener('mousewheel', onMousewheel);
    };
  });
}

export interface Position {
  left: number;
  top: number;
}

type Target = BasicTarget<HTMLElement | Document>;
type ScrollPositionUpdateHandler = (val: Position) => boolean;

export function useScrollPos(target?: Target, shouldUpdate: ScrollPositionUpdateHandler = () => true): Position {
  const [position, setPosition] = useState<Position>({
    left: NaN,
    top: NaN,
  });

  const shouldUpdatePersist = usePersistFn(shouldUpdate);

  useEffect(() => {
    const el = ElementUtil.getTargetElement(target, document);
    if (!el) return;

    function updatePosition(currentTarget: Target) {
      let newPosition: Position;
      if (currentTarget === document) {
        if (!document.documentElement) return;
        newPosition = {
          left: document.documentElement.scrollLeft,
          top: document.documentElement.scrollTop,
        };
      } else {
        newPosition = {
          left: (currentTarget as HTMLElement).scrollLeft,
          top: (currentTarget as HTMLElement).scrollTop,
        };
      }
      if (shouldUpdatePersist(newPosition)) setPosition(newPosition);
    }

    updatePosition(el as Target);

    function listener(event: Event) {
      if (!event.target) return;
      updatePosition(event.target as Target);
    }
    el.addEventListener('scroll', listener);
    return () => {
      el.removeEventListener('scroll', listener);
    };
  }, [target, shouldUpdatePersist]);

  return position;
}
